import { FC, useState } from 'react';
import { Form, Input, Select, AutoComplete } from 'antd';
import usePlacesAutocomplete, { getGeocode, getLatLng, getZipCode } from 'use-places-autocomplete';
import { IDesignerPersonalDetails, IAddress } from '../constants';
import { AuthButtonComponent } from '../components/auth-button/auth-button.component';
import { NiceDatePicker } from 'components/commons/nice-date-picker';
import moment from 'moment';
import { PasswordField } from '../components/password-field/password-field';

const { Option } = Select;

interface IDesignerPersonalDetailsComponentProps {
  initialValues?: IDesignerPersonalDetails;
  submittedValues: Function;
  handleNextButton: Function;
}

const initialAddressState: IAddress = {};

const PersonalDetails: FC<IDesignerPersonalDetailsComponentProps> = ({
  initialValues,
  submittedValues,
  handleNextButton,
}) => {
  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {},
    debounce: 300,
    callbackName: 'initMap',
  });

  const [addressObj, setAddressObj] = useState(initialValues?.address || initialAddressState);

  const [form] = Form.useForm();

  const onFinish = (values: IDesignerPersonalDetails) => {
    submittedValues('personalDetails', {
      ...values,
      address: {
        ...addressObj,
        zipCode: values.address.postCode ? values.address.postCode : addressObj?.postCode,
        postCode: values.address.postCode ? values.address.postCode : addressObj?.postCode,
        country: values.address.country ? values.address.country : addressObj?.country,
      },
    });
    handleNextButton();
  };

  const onFinishFailed = ({ errorFields }: any) => {
    form.scrollToField(errorFields[0].name, { block: 'center' });
  };

  const handleAddressChange = (val: any) => {
    setValue(val);
  };

  const handleAddressSelect = ({ description }: any) => {
    // When user selects a place, we can replace the keyword without request data from API
    // by setting the second parameter to "false"
    // setValue(description, false);
    clearSuggestions();

    // Get latitude and longitude via utility functions
    getGeocode({ address: description })
      .then(async (results) => {
        const latLng = await getLatLng(results[0]);
        const zipCode = await getZipCode(results[0], false);
        return { ...latLng, zipCode };
      })
      .then(({ lat, lng, zipCode }) => {
        const address: IAddress = {};
        address.formattedAddress = description;
        address.latitude = lat;
        address.longitude = lng;
        address.zipCode = zipCode ?? '';
        address.postCode = zipCode ?? '';
        setAddressObj(address);
        form.setFieldsValue({
          address,
        });
      })
      .catch((error) => {
        console.log('😱 Error: ', error);
      });
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      layout="vertical"
      size="large"
      autoComplete="off"
      className="mt-3 fadein animation-duration-500"
    >
      <div className="grid">
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name="firstName"
            label="First Name *"
            rules={[
              {
                required: true,
                message: 'Please input first name',
              },
            ]}
          >
            <Input className="custom-input" placeholder="John" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name="lastName"
            label="Last Name *"
            // hasFeedback
            rules={[
              {
                required: true,
                message: 'Please input last name',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Doe" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name={['email', 'email']}
            label="Email Address *"
            rules={[
              {
                required: true,
                message: 'Please enter your email address',
              },
              { type: 'email', message: 'Please enter your full email address' },
            ]}
            normalize={(fieldValue) => fieldValue?.toLowerCase()}
          >
            <Input className="custom-input" placeholder="Enter your email address" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name="dob"
            label="Date of birth *"
            rules={[
              {
                required: true,
                message: 'Please input date of birth',
              },
            ]}
          >
            <NiceDatePicker disabledDate={(current) => current && current > moment()} className="custom-input w-full" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="designLabName" label="Design Lab Name">
            <Input className="custom-input" placeholder="Enter Design Lab Name" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <PasswordField form={form} />
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name="confirmPassword"
            label="Confirm Password *"
            dependencies={['password']}
            // hasFeedback
            rules={[
              {
                required: true,
                message: 'Please input confirm password',
              },
              ({ getFieldValue }) => ({
                validator(_, fieldValue) {
                  if (!fieldValue || getFieldValue('password') === fieldValue) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords that you entered do not match!'));
                },
              }),
            ]}
          >
            <Input.Password className="custom-input" placeholder="Enter Confirm Password" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item label="Mobile Number *">
            <Input.Group compact className="flex">
              <Form.Item name={['mobile', 'ext']} noStyle rules={[{ required: true, message: 'ext is required' }]}>
                <Select className="custom-input" style={{ width: '97px' }} placeholder="ext">
                  <Option value="+44">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/555/555417.png"
                      alt="UK falg"
                      style={{ width: 20 }}
                      className="mr-1"
                    />{' '}
                    +44
                  </Option>
                  <Option value="+1">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/206/206626.png"
                      alt="US falg"
                      style={{ width: 20 }}
                      className="mr-1"
                    />{' '}
                    +1
                  </Option>
                </Select>
              </Form.Item>
              <Form.Item
                name={['mobile', 'number']}
                noStyle
                rules={[{ required: true, message: 'Please enter your mobile number' }]}
              >
                <Input className="custom-input flex-auto" placeholder="Enter your mobile number" />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item
            name={['address', 'formattedAddress']}
            label="Business Address *"
            rules={[{ required: true, message: 'Please enter business address' }]}
          >
            {/* TODO: need to create reusable component for search address */}
            <AutoComplete
              className="custom-input"
              value={value}
              onChange={handleAddressChange}
              placeholder="Enter clinic address"
            >
              {status === 'OK' &&
                data?.map((suggestion) => {
                  const {
                    place_id,
                    structured_formatting: { main_text, secondary_text },
                  } = suggestion;
                  return (
                    <AutoComplete.Option
                      className="places-options"
                      key={place_id}
                      onClick={() => handleAddressSelect(suggestion)}
                      value={`${main_text} ${secondary_text || ''}`}
                    >
                      <div key={place_id} onClick={() => handleAddressSelect(suggestion)}>
                        <strong>{main_text}</strong> <small>{secondary_text}</small>
                      </div>
                    </AutoComplete.Option>
                  );
                })}
            </AutoComplete>
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name={['address', 'postCode']} label="Business Postcode">
            <Input className="custom-input" placeholder="Enter your postcode" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name={['address', 'country']} label="Business Country">
            <Input className="custom-input" placeholder="Enter country" />
          </Form.Item>
        </div>
      </div>

      <div className="steps-action">
        <Form.Item>
          <AuthButtonComponent>Next</AuthButtonComponent>
        </Form.Item>
      </div>
    </Form>
  );
};

export { PersonalDetails };
